import { apiGet, apiPost } from '@/utils/api';

export type SolutionType = 'academic' | 'finance' | 'marketing';
export type RequestType = 'ACTIVATE' | 'DEACTIVATE';
export type RequestStatus = 'SOLUTION_PENDING' | 'SOLUTION_APPROVED' | 'SOLUTION_REJECTED' | 'SOLUTION_CANCELLED';

export interface SolutionChangeRequest {
  id: string;
  tenantId: string;
  tenantName?: string;
  requestedById: string;
  requestedByName?: string;
  requestType: RequestType;
  solution: SolutionType;
  status: RequestStatus;
  reviewedById?: string;
  reviewedByName?: string;
  reviewedAt?: string;
  rejectionReason?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSolutionRequestDto {
  solution: SolutionType;
  requestType: RequestType;
}

export interface RejectSolutionRequestDto {
  reason: string;
}

/**
 * Create a new solution change request
 */
export const createSolutionRequest = async (
  dto: CreateSolutionRequestDto
): Promise<SolutionChangeRequest> => {
  return apiPost<SolutionChangeRequest>('/solution-requests', dto);
};

/**
 * Get my institution's solution requests
 */
export const getMyRequests = async (): Promise<SolutionChangeRequest[]> => {
  return apiGet<SolutionChangeRequest[]>('/solution-requests/my-requests');
};

/**
 * Get pending request for a specific solution
 */
export const getPendingRequestForSolution = async (
  solution: SolutionType
): Promise<SolutionChangeRequest | null> => {
  return apiGet<SolutionChangeRequest | null>(`/solution-requests/pending/${solution}`);
};

/**
 * Get all pending requests (Super Admin)
 */
export const getAllPendingRequests = async (): Promise<SolutionChangeRequest[]> => {
  return apiGet<SolutionChangeRequest[]>('/solution-requests/pending');
};

/**
 * Get count of pending requests (Super Admin)
 */
export const getPendingRequestsCount = async (): Promise<number> => {
  const response = await apiGet<{ count: number }>('/solution-requests/pending-count');
  return response.count;
};

/**
 * Approve a solution request (Super Admin)
 */
export const approveRequest = async (requestId: string): Promise<SolutionChangeRequest> => {
  return apiPost<SolutionChangeRequest>(`/solution-requests/${requestId}/approve`);
};

/**
 * Reject a solution request (Super Admin)
 */
export const rejectRequest = async (
  requestId: string,
  dto: RejectSolutionRequestDto
): Promise<SolutionChangeRequest> => {
  return apiPost<SolutionChangeRequest>(`/solution-requests/${requestId}/reject`, dto);
};

/**
 * Cancel a pending request
 */
export const cancelRequest = async (requestId: string): Promise<SolutionChangeRequest> => {
  return apiPost<SolutionChangeRequest>(`/solution-requests/${requestId}/cancel`);
};

/**
 * Get solution label
 */
export const getSolutionLabel = (solution: SolutionType): string => {
  const labels: Record<SolutionType, string> = {
    academic: 'Gestion Academique',
    finance: 'Gestion Financiere',
    marketing: 'Gestion Marketing',
  };
  return labels[solution] || solution;
};

/**
 * Get request type label
 */
export const getRequestTypeLabel = (type: RequestType): string => {
  return type === 'ACTIVATE' ? 'Activation' : 'Desactivation';
};

/**
 * Get status label and color
 */
export const getStatusConfig = (status: RequestStatus): { label: string; color: string; bgColor: string } => {
  const configs: Record<RequestStatus, { label: string; color: string; bgColor: string }> = {
    SOLUTION_PENDING: { label: 'En attente', color: 'text-amber-700', bgColor: 'bg-amber-100' },
    SOLUTION_APPROVED: { label: 'Approuvee', color: 'text-green-700', bgColor: 'bg-green-100' },
    SOLUTION_REJECTED: { label: 'Refusee', color: 'text-red-700', bgColor: 'bg-red-100' },
    SOLUTION_CANCELLED: { label: 'Annulee', color: 'text-gray-700', bgColor: 'bg-gray-100' },
  };
  return configs[status] || { label: status, color: 'text-gray-700', bgColor: 'bg-gray-100' };
};
